import { useNavigate, Link } from "react-router-dom";
import logo from "../../assets/logoGearXpert.png";
import { useTranslation } from "react-i18next";

export default function Footer() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const FooterLink = ({ to, children }) => (
    <button
      type="button"
      onClick={() => {
        navigate(to);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }}
      className="text-sm text-slate-500 hover:text-primary transition-colors font-medium text-left focus:outline-none focus:ring-2 focus:ring-primary/30 rounded"
    >
      {children}
    </button>
  );

  const ExternalLink = ({ to, children }) => (
    <Link
      to={to}
      target="_blank"
      rel="noreferrer"
      className="text-sm text-slate-500 hover:text-primary transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-primary/30 rounded"
    >
      {children}
    </Link>
  );

  return (
    <footer className="bg-white border-t border-slate-200 mt-12">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-10 py-10">
        {/* Top */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          {/* Brand */}
          <div className="md:col-span-5">
            <div
              className="flex items-center gap-3 cursor-pointer w-fit group"
              onClick={() => navigate("/")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && navigate("/")}
              aria-label="Go to homepage"
            >
              <img src={logo} alt="GearXpert Logo" className="h-20 md:h-32 lg:h-40 w-auto object-contain transition-transform group-hover:scale-110" />
            </div>

            <p className="mt-3 text-xs md:text-sm text-slate-500 leading-relaxed max-w-md">
              {t('footer.description')}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                <span className="material-symbols-outlined text-[16px]">
                   verified
                </span>
                {t('footer.security')}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                <span className="material-symbols-outlined text-[16px]">
                  bolt
                </span>
                {t('footer.fast_payment')}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                <span className="material-symbols-outlined text-[16px]">
                  support_agent
                </span>
                {t('footer.support')}
              </span>
            </div>
          </div>

          {/* Links */}
          <div className="md:col-span-4 grid grid-cols-2 gap-8">
            <div>
              <h3 className="text-sm font-bold text-slate-900">{t('footer.platform')}</h3>
              <div className="mt-3 flex flex-col gap-2">
                <FooterLink to="/products">{t('footer.browse_devices')}</FooterLink>
                <FooterLink to="/pricing">{t('footer.pricing')}</FooterLink>
                <FooterLink to="/vouchers">{t('footer.vouchers')}</FooterLink>
                <FooterLink to="/tracking">{t('footer.order_tracking')}</FooterLink>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-bold text-slate-900">{t('footer.company')}</h3>
              <div className="mt-3 flex flex-col gap-2">
                <FooterLink to="/about">{t('footer.about')}</FooterLink>
                <FooterLink to="/blog">{t('footer.blog')}</FooterLink>
                <FooterLink to="/contact">{t('footer.contact')}</FooterLink>
                <FooterLink to="/faq">{t('footer.faq')}</FooterLink>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <h3 className="text-sm font-bold text-slate-900">{t('footer.contact_us')}</h3>

            <div className="mt-3 space-y-2">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span className="material-symbols-outlined text-[18px] text-slate-400">
                  mail
                </span>
                <span>support@gearxpert.io</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span className="material-symbols-outlined text-[18px] text-slate-400">
                  schedule
                </span>
                <span>{t('footer.working_hours')}</span>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3">
              <ExternalLink to="https://github.com/">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 text-slate-500 hover:text-primary hover:border-primary/30 transition-colors">
                  <span className="material-symbols-outlined text-[20px]">
                    code
                  </span>
                </span>
              </ExternalLink>

              <ExternalLink to="https://www.linkedin.com/">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 text-slate-500 hover:text-primary hover:border-primary/30 transition-colors">
                  <span className="material-symbols-outlined text-[20px]">
                    work
                  </span>
                </span>
              </ExternalLink>

              <ExternalLink to="https://www.facebook.com/">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 text-slate-500 hover:text-primary hover:border-primary/30 transition-colors">
                  <span className="material-symbols-outlined text-[20px]">
                    public
                  </span>
                </span>
              </ExternalLink>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-10 border-t border-slate-200 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-400 text-sm font-medium text-center md:text-left">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <FooterLink to="/privacy">{t('footer.privacy')}</FooterLink>
            <FooterLink to="/terms">{t('footer.terms')}</FooterLink>
            <FooterLink to="/support">{t('footer.support')}</FooterLink>

            <span className="hidden sm:inline-flex items-center gap-2 text-xs font-semibold text-slate-500">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              {t('footer.system_status')}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
